import React, { useCallback } from 'react';
import type { FundProxy, FundToProxy } from 'venn-components';
import { ProxyCell, SidePanelOverlay, BulkProxyPicker } from 'venn-components';
import styled from 'styled-components';
import type { SearchResultWithUIState, Id, SearchState } from './types';
import { getFrequency } from './utils';
import { emptyRenderer } from './Shared';
import { BulkHoldingsProxyPicker } from '../../../../../venn-components/src/modals/pickers/holdings-proxy-picker/BulkHoldingsProxyPicker';

export interface ProxyRenderProps {
  item: SearchResultWithUIState;
  showModal?: boolean;
  onModalClose: () => void;
  setIdWithMenuOpen: (id: Id) => void;
  updateData: React.Dispatch<React.SetStateAction<SearchState>>;
  refetchSearch: (hideLoading?: boolean) => void;
  isHoldingProxy?: boolean;
}

const ProxyRenderer = ({
  item,
  setIdWithMenuOpen,
  updateData,
  refetchSearch,
  showModal,
  onModalClose,
  isHoldingProxy,
}: ProxyRenderProps) => {
  const {
    proxyName,
    hasFactorAnalysis,
    proxyId,
    proxyType,
    fundId,
    name,
    proxyCategory,
    investmentSource,
    startRange,
    endRange,
    updated,
  } = item;
  const onProxyChange = useCallback(
    (proxyId: string | undefined, proxyName: string | undefined) => {
      // Show cell loading
      updateData((prev) => {
        const newData = [...prev.results];
        return {
          ...prev,
          results: newData.map((dataItem) => {
            if (dataItem.fundId === fundId) {
              if (isHoldingProxy) {
                return { ...dataItem, isUpdating: true }; // proxyId and proxyName are older fields for returns proxy which is why they're not being set here
              }
              return {
                ...dataItem,
                proxyId,
                proxyName,
                isUpdating: true,
              };
            }
            return dataItem;
          }),
        };
      });
      // Update table without loader
      refetchSearch(true);
      onModalClose?.();
    },
    [updateData, refetchSearch, onModalClose, fundId, isHoldingProxy],
  );

  if (investmentSource === 'CUSTOM') {
    return emptyRenderer();
  }

  const proxy =
    !proxyType || !proxyName
      ? null
      : {
          name: proxyName,
          id: proxyId,
          proxyId,
          proxyType,
          // the library search returns nulls so use a trick to convert it to undefined
          proxyCategory: proxyCategory || undefined,
          extrapolate: item.extrapolated,
          numLags: item.proxyNumLags,
        };

  const investment: FundToProxy = {
    id: fundId,
    name,
    startRange,
    endRange,
    frequency: getFrequency(item.frequency),
    returnFrequency: item.frequency,
    updated,
    userUploaded: item.userUploaded,
    investmentSource: item.investmentSource,
    interpolatable: item.interpolatable,
    hasMinDataForInterpolation: item.hasMinDataForInterpolation,
    unproxiedFrequency: item.unproxiedFrequency,
    unproxiedStartRange: item.unproxiedStartRange,
    unproxiedEndRange: item.unproxiedEndRange,
    proxyNumLags: item.proxyNumLags,
    proxyId: item.proxyId,
    proxyInfos: item.proxyInfos,
    extrapolate: item.extrapolated,
    assetType: item.assetType,
    live: item.live,
    dataSource: item.dataSource,
    symbol: item.symbol,
  };

  if (showModal) {
    return (
      <SidePanelOverlay.Root
        width={1062}
        margin={0}
        noPadding
        side="right"
        isOpen
        handleClose={onModalClose}
        content={
          isHoldingProxy ? (
            <BulkHoldingsProxyPicker
              investments={[investment]}
              onProxyChange={(appliedProxies) =>
                onProxyChange(appliedProxies[0]!.proxyId, appliedProxies[0]!.proxyName)
              }
            />
          ) : (
            <BulkProxyPicker
              investments={[investment]}
              onProxyChange={(appliedProxies) =>
                onProxyChange(appliedProxies[0]!.proxyId, appliedProxies[0]!.proxyName)
              }
            />
          )
        }
        hideTopBackButton
      />
    );
  }

  return (
    // prevent library row clicks and cursor styling from going through
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <ProxyCell
        proxy={proxy}
        investment={investment}
        hoverAddProxy={hasFactorAnalysis}
        onProxyChange={(proxy?: FundProxy) => onProxyChange(proxy?.proxyId, proxy?.name)}
        onOpen={(id: string) => setIdWithMenuOpen({ fundId: id })}
        onClose={() => setIdWithMenuOpen({ fundId: undefined })}
        loading={item.isUpdating}
      />
    </Wrapper>
  );
};

export default ProxyRenderer;

const Wrapper = styled.div`
  cursor: auto;
`;
